import { AppDataProvider, BookingData, BookingTool } from "./AppDataProvider/AppDataProvider";
import { FocusTrap, emitEvent, getFirstStep, getWidgetType, useViewportSize } from "./utils";
import React, { ReactElement, useEffect, useRef, useState } from "react";
import { getCustomFonts, getTheme, media, paddingBottom, paddingTop, paddingX } from "./common/styles";
import styled, { ThemeProvider, css } from "styled-components";
import { Footer } from "./Footer/Footer";
import { Header } from "./Header/Header";
import { Mixpanel } from "./mixpanel/mixpanel";
import { NotificationsAndDialogs } from "./NotificationsAndDialogs/NotificationsAndDialogs";
import { ProgressTracker } from "./common/components";
import { Steps } from "./Steps/Steps";
import { useTranslation } from "react-i18next";
type ContentContainerProps = {
  bookingData: BookingData;
  bookingTool: BookingTool;
  inPopoverMode?: boolean;
  userLocation?: string;
};
export function ContentContainer(props: ContentContainerProps): ReactElement {
  const {
    bookingTool,
    bookingData,
    inPopoverMode,
    userLocation
  } = props;
  const {
    i18n
  } = useTranslation();
  const viewport = useViewportSize();
  const [userHasClicked, setUserHasClicked] = useState(false);
  const recordFirstClick = (event: React.MouseEvent<HTMLElement>) => {
    if (userHasClicked) return;

    /**
     * We need to check if the first clicked element was the close button.
     * We use event.target.closest() as the event target could be the
     * .popoverCloseButton button itself or a child of the button.
     */
    if (event.target instanceof Element) {
      const targetIsCloseButtonOrChild = event.target.closest(".popoverCloseButton");
      /**
       * If the first selected element was the close button,
       * we do not want to emit the USER_ENGAGED event.
       */
      if (targetIsCloseButtonOrChild) return;
    }
    if (iFrameId) {
      emitEvent({
        data: {
          iFrameId: iFrameId
        },
        name: "USER_ENGAGED"
      });
    }
    Mixpanel.track({
      name: "Engaged"
    });
    setUserHasClicked(true);
  };
  useEffect(() => {
    const widgetOrganisations = bookingTool.organisations.edges.map(({
      node
    }) => node.slug);
    Mixpanel.register({
      initialLanguage: i18n.language,
      sessionId: bookingData.sessionId,
      widgetId: bookingTool._id,
      widgetOrganisations,
      widgetType: getWidgetType(bookingData, inPopoverMode)
    });
    Mixpanel.track({
      name: "App loaded"
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const progressInHeader = viewport.width >= media.thresholds.progressInHeader && viewport.width < media.thresholds.desktop;
  const urlParams = new URLSearchParams(window.location.search);
  const iFrameId = urlParams.get("iFrameId") ?? undefined;
  const reservationOnly = urlParams.get("reservationOnly") ? true : false;
  const {
    customColours,
    fonts
  } = bookingTool;
  const {
    customFonts,
    customFontFaces
  } = getCustomFonts(fonts);
  const theme = getTheme({
    customColours,
    customFonts
  });
  const currentStep = getFirstStep({
    bookingData: bookingData,
    bookingTool: bookingTool
  });
  const scrollableRef = useRef<HTMLDivElement>(null);
  const scrollToTop = () => {
    if (userHasClicked && scrollableRef?.current?.scrollIntoView) {
      scrollableRef.current.scrollIntoView(true);
    }
  };
  return <>
      <style>{customFontFaces}</style>

      <FocusTrap active={inPopoverMode} data-sentry-element="FocusTrap" data-sentry-source-file="ContentContainer.tsx">
        <Container data-testid="appContainer" onClick={e => recordFirstClick(e)} inPopoverMode={inPopoverMode} ref={scrollableRef} data-sentry-element="Container" data-sentry-source-file="ContentContainer.tsx">
          <ThemeProvider theme={theme} data-sentry-element="ThemeProvider" data-sentry-source-file="ContentContainer.tsx">
            <AppDataProvider iFrameId={iFrameId} bookingTool={bookingTool} bookingData={bookingData} currentStep={currentStep} userLocation={userLocation} reservationOnly={reservationOnly} data-sentry-element="AppDataProvider" data-sentry-source-file="ContentContainer.tsx">
              <NotificationsAndDialogs data-sentry-element="NotificationsAndDialogs" data-sentry-source-file="ContentContainer.tsx" />
              <Header data-sentry-element="Header" data-sentry-source-file="ContentContainer.tsx" />
              <BodyContainer data-sentry-element="BodyContainer" data-sentry-source-file="ContentContainer.tsx">
                {!progressInHeader && <ProgressTracker />}
                <Steps scrollToTop={scrollToTop} data-sentry-element="Steps" data-sentry-source-file="ContentContainer.tsx" />
              </BodyContainer>
              {!bookingTool.hideAppointeddFooter && <Footer />}
            </AppDataProvider>
          </ThemeProvider>
        </Container>
      </FocusTrap>
    </>;
}
const Container = styled.div<{
  inPopoverMode?: boolean;
}>`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  align-self: center;
  ${({
  inPopoverMode
}) => inPopoverMode ? css`
          overflow: scroll;
          ${paddingTop.s}
          ${paddingX.m}
          @media screen and (width >= 1200px) {
            ${paddingTop.m}
            ${paddingX.l}
          }
        ` : null}
`;
const BodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  ${paddingBottom.s}
  @media screen and (width >= 1200px) {
    ${paddingBottom.m}
  }
  @media ${media.desktop} {
    flex-direction: row;
    align-items: flex-start;
  }
`;
export default ContentContainer;